import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useDarkMode } from '../../context/DarkModeContext';
import { useDarkClasses } from '../../hooks/useDarkClasses';
import { useRoleTheme } from '../../hooks/useRoleTheme';
import { SaveIcon, UploadIcon, PlusIcon, XIcon } from 'lucide-react';
import BackButton from '../../components/UI/BackButton';
const BookEdit = () => {
  const {
    id
  } = useParams();
  const navigate = useNavigate();
  const {
    darkMode
  } = useDarkMode();
  const dk = useDarkClasses();
  const rt = useRoleTheme();
  const isNew = !id;
  const [formData, setFormData] = useState({
    title: '',
    author: '',
    description: '',
    genre: '',
    subgenre: '',
    price: '',
    isbn: '',
    pageCount: '',
    publishDate: '',
    tags: [] as string[],
    contentWarnings: [] as string[]
  });
  const [tagInput, setTagInput] = useState('');
  const handleAddTag = () => {
    if (tagInput.trim()) {
      setFormData(prev => ({
        ...prev,
        tags: [...prev.tags, tagInput.trim()]
      }));
      setTagInput('');
    }
  };
  const handleRemoveTag = (index: number) => {
    setFormData(prev => ({
      ...prev,
      tags: prev.tags.filter((_, i) => i !== index)
    }));
  };
  const inputClass = `w-full rounded-md px-3 py-2 ${dk.inputOnCard} ${rt.inputFocus}`;
  const labelClass = `block text-sm font-medium mb-1 ${dk.textSecondary}`;
  return <div className={`min-h-screen py-8 ${dk.page} ${dk.textPrimary}`} data-id="element-18570" data-mp="1q0k5ox745ps4">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-18571" data-mp="1l0o2v1a5k4e5">
        <div className="flex items-center justify-between mb-8" data-id="element-18572" data-mp="1l28sqdliuz5o">
          <div className="flex items-center" data-id="element-18573" data-mp="1zqdexrjs9st7">
            <div className="mr-4" data-id="element-18574" data-mp="1zr534hb1wo9g">
              <BackButton data-id="element-18575" data-mp="1uybwfnw0v5uq" />
            </div>
            <h1 className={`text-3xl font-bold ${dk.textHeading}`} data-id="element-18576" data-mp="1uxaldjf0ajd0">
              {isNew ? 'Add New Book' : 'Edit Book Details'}
            </h1>
          </div>
          <div className="flex space-x-3" data-id="element-18577" data-mp="12apqwa38hr58">
            <button className={`px-4 py-2 border rounded-md ${dk.card}`} data-id="element-18578" data-mp="127tuiq71baiw">
              Save Draft
            </button>
            <button className={`flex items-center px-4 py-2 rounded-md text-white ${rt.primaryButton}`} data-id="element-18579" data-mp="1vzni07hpvoig">
              <SaveIcon className="h-4 w-4 mr-2" data-id="element-18580" data-mp="20q3zq3sklwam" />
              Publish Changes
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8" data-id="element-18581" data-mp="25ooydys6c7wy">
          {/* Left Column - Cover Image */}
          <div className="lg:col-span-1" data-id="element-18582" data-mp="191ajw762yl1k">
            <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-18583" data-mp="1d09mvm8947qd">
              <h3 className={`font-medium mb-4 ${dk.textHeading}`} data-id="element-18584" data-mp="1hzvyxsccfhxl">
                Book Cover
              </h3>
              <div className={`aspect-[2/3] rounded-lg border-2 border-dashed flex flex-col items-center justify-center ${dk.cardElevated} ${dk.border}`} data-id="element-18585" data-mp="1mypfxuqu9r2i">
                <UploadIcon className={`h-12 w-12 mb-2 ${dk.textMuted}`} data-id="element-18586" data-mp="2ye5lqi0xsymb" />
                <p className={`text-sm ${dk.textSecondary}`} data-id="element-18587" data-mp="2yg8nfzbgcg6v">
                  Click to upload cover
                </p>
                <p className={`text-xs mt-1 ${dk.textMuted}`} data-id="element-18588" data-mp="hrpiks52t8v3">
                  JPG, PNG up to 5MB
                </p>
              </div>
              <button className={`mt-4 w-full py-2 border rounded-md text-sm font-medium hover:bg-gray-50 dark:hover:bg-gray-700 ${dk.border} ${dk.textPrimary}`} data-id="element-18589" data-mp="czxqgd69uv2u">
                Change Cover
              </button>
            </div>
          </div>

          {/* Right Column - Form Fields */}
          <div className="lg:col-span-2 space-y-6" data-id="element-18590" data-mp="3v5126gqqu5c8">
            {/* Basic Info */}
            <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-18591" data-mp="3q8ajrmrguxnn">
              <h3 className={`font-medium mb-4 text-lg ${dk.textHeading}`} data-id="element-18592" data-mp="3l97q0ygxsduz">
                Basic Information
              </h3>
              <div className="grid grid-cols-1 gap-6" data-id="element-18593" data-mp="1ofmdf4jizu3n">
                <div data-id="element-18594" data-mp="1jhb5izcsjto2">
                  <label className={labelClass} data-id="element-18595" data-mp="1ji4em6z82v9d">Book Title</label>
                  <input type="text" className={inputClass} placeholder="Enter book title" data-id="element-18596" data-mp="1rhr3zxj3l0k2" />
                </div>
                <div className="grid grid-cols-2 gap-4" data-id="element-18597" data-mp="1hjl6rt88kilj">
                  <div data-id="element-18598" data-mp="1hj1na82xtm5e">
                    <label className={labelClass} data-id="element-18599" data-mp="1hlnta3a6jtli">Genre</label>
                    <select className={inputClass} data-id="element-18600" data-mp="1mhcx4it8fwnm">
                      <option data-id="element-18601" data-mp="18r9ioe0lh78q">Select Genre</option>
                      <option data-id="element-18602" data-mp="18qhmp23357hx">Romance</option>
                      <option data-id="element-18603" data-mp="1dhzkjh9taryo">Fantasy</option>
                      <option data-id="element-18604" data-mp="1dirigyqvt1s4">Thriller</option>
                    </select>
                  </div>
                  <div data-id="element-18605" data-mp="yu4vqbyxd7qv">
                    <label className={labelClass} data-id="element-18606" data-mp="yuwn1alhojbc">Subgenre</label>
                    <input type="text" className={inputClass} placeholder="e.g. Dark Romance" data-id="element-18607" data-mp="13t7zlt3657x9" />
                  </div>
                </div>
                <div data-id="element-18608" data-mp="ttgznbvua0ps">
                  <label className={labelClass} data-id="element-18609" data-mp="tvtjjul5ql5h">Description</label>
                  <textarea rows={6} className={inputClass} placeholder="Book description..." data-id="element-18610" data-mp="b688un3p3e56" />
                </div>
              </div>
            </div>

            {/* Pricing & Details */}
            <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-18611" data-mp="f70ihhghbl1z">
              <h3 className={`font-medium mb-4 text-lg ${dk.textHeading}`} data-id="element-18612" data-mp="u4kg23b1qdy4">
                Pricing & Details
              </h3>
              <div className="grid grid-cols-2 gap-6" data-id="element-18613" data-mp="p6967skr43fw">
                <div data-id="element-18614" data-mp="p3n0lyhf7y0b">
                  <label className={labelClass} data-id="element-18615" data-mp="12xe1kih369kf">Price ($)</label>
                  <input type="number" step="0.01" className={inputClass} placeholder="0.00" data-id="element-18616" data-mp="12yyu62jubifn" />
                </div>
                <div data-id="element-18617" data-mp="17vz3cmbzl1az">
                  <label className={labelClass} data-id="element-18618" data-mp="17wh7040zahsx">Page Count</label>
                  <input type="number" className={inputClass} placeholder="e.g. 350" data-id="element-18619" data-mp="17u4fp09byv37" />
                </div>
                <div data-id="element-18620" data-mp="9kefpa4vcaiv">
                  <label className={labelClass} data-id="element-18621" data-mp="ej7quvwonpfv">ISBN (Optional)</label>
                  <input type="text" className={inputClass} placeholder="ISBN-13" data-id="element-18622" data-mp="ehwp11mssrre" />
                </div>
                <div data-id="element-18623" data-mp="o898eqivmevy">
                  <label className={labelClass} data-id="element-18624" data-mp="ob556bizmxit">Publish Date</label>
                  <input type="date" className={inputClass} data-id="element-18625" data-mp="t6sseldwtglo" />
                </div>
              </div>
            </div>

            {/* Tags & Metadata */}
            <div className={`p-6 rounded-xl shadow-sm border ${dk.card}`} data-id="element-18626" data-mp="128trzowm8eqi">
              <h3 className={`font-medium mb-4 text-lg ${dk.textHeading}`} data-id="element-18627" data-mp="126plcz7c11mj">
                Tags & Tropes
              </h3>
              <div className="mb-4" data-id="element-18628" data-mp="33zx0a7j19h7w">
                <div className="flex gap-2 mb-2" data-id="element-18629" data-mp="340ydahjm0ds9">
                  <input type="text" value={tagInput} onChange={e => setTagInput(e.target.value)} className={`flex-1 ${inputClass}`} placeholder="Add a tag or trope..." onKeyDown={e => e.key === 'Enter' && handleAddTag()} data-id="element-18630" data-mp="2wgnq4c2pdehs" />

                  <button onClick={handleAddTag} className={`px-4 py-2 rounded-md ${dk.cardElevated}`} data-id="element-18631" data-mp="2htku69u13dwk">
                    <PlusIcon className={`h-5 w-5 ${dk.textPrimary}`} data-id="element-18632" data-mp="3ag5lxbef1s0n" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2" data-id="element-18633" data-mp="35g10d6yni73k">
                  {formData.tags.map((tag, index) => <span key={index} className={`inline-flex items-center px-3 py-1 rounded-full text-sm ${rt.bgMedium} ${rt.textPrimary}`} data-id="element-18634" data-mp="31fh6cc9djjln" data-mpi={index}>
                      {tag}
                      <button onClick={() => handleRemoveTag(index)} className={`ml-2 hover:opacity-75`} data-id="element-18635" data-mp="3p5btaaa00arj">
                        <XIcon className="h-3 w-3" data-id="element-18636" data-mp="3u2bzcl6uldin" />
                      </button>
                    </span>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
};
export default BookEdit;